import type {
  AdminAuditListQuery,
  AdminJobListQuery,
  AdminOrgListQuery,
  AdminUserListQuery,
  AdminWorkerListQuery,
} from "@rivets/core/admin";

/** Centralized react-query key builders so the socket patcher and the
 * components that fetch data agree on shape without importing each other. */
export const qk = {
  me: () => ["me"] as const,
  org: (orgId: string) => ["org", orgId] as const,
  repos: (orgId: string) => ["repos", orgId] as const,
  homeStats: (orgId: string) => ["home-stats", orgId] as const,
  github: (orgId: string) => ["github", orgId] as const,
  githubRepos: (orgId: string) => ["github-repos", orgId] as const,
  integration: (orgId: string, provider: string) => ["integration", orgId, provider] as const,
  workers: (orgId: string) => ["workers", orgId] as const,
  automations: (orgId: string) => ["automations", orgId] as const,
  /** The signed-in member's inbox in one organization (first page). */
  inbox: (orgId: string) => ["inbox", orgId] as const,
  /** Whether the signed-in member follows a task. */
  taskFollow: (worktreeId: string) => ["task-follow", worktreeId] as const,
  automationOptions: (orgId: string) => ["automation-options", orgId] as const,
  automationRuns: (orgId: string, automationId: string) => ["automation-runs", orgId, automationId] as const,
  repoBranches: (repoId: string) => ["repo-branches", repoId] as const,
  repoPullRequests: (repoId: string) => ["repo-pull-requests", repoId] as const,
  linearIssues: (orgId: string, query: string) => ["linear-issues", orgId, query] as const,
  worktrees: (repoId: string) => ["worktrees", repoId] as const,
  worktreeParticipants: (worktreeId: string) => ["worktree-participants", worktreeId] as const,
  worktree: (worktreeId: string) => ["worktree", worktreeId] as const,
  /** The latest `SnapshotSummary` (no diff text, no path list), or null. */
  snapshot: (worktreeId: string) => ["snapshot", worktreeId] as const,
  /** One file's diff in one snapshot: immutable, so never refetched. */
  snapshotDiff: (snapshotId: string, scope: string, path: string) => ["snapshot-diff", snapshotId, scope, path] as const,
  /** A snapshot's full path list, loaded lazily for transcript file links. */
  snapshotPaths: (snapshotId: string) => ["snapshot-paths", snapshotId] as const,
  prWorkspace: (worktreeId: string) => ["pr-workspace", worktreeId] as const,
  pullRequestStatus: (worktreeId: string) => ["pull-request-status", worktreeId] as const,
  previewServices: (worktreeId: string) => ["preview-services", worktreeId] as const,
  nativeDevices: () => ["native-devices"] as const,
  /** The account's sign-in sessions (account-wide, not per org). */
  authSessions: () => ["auth-sessions"] as const,
  /** Personal MCP/API tokens. Org-scoped because a token belongs to one
   * membership, so switching orgs shows a different list. */
  apiTokens: (orgId: string) => ["api-tokens", orgId] as const,
  /** The member's assistant workspace and its conversations. */
  assistant: (orgId: string) => ["assistant", orgId] as const,
  /** Slack/Teams installs for the org. */
  channels: (orgId: string) => ["channels", orgId] as const,
  /** Whether this member's account is bound to their identity in one channel. */
  channelIdentity: (orgId: string, provider: string) => ["channel-identity", orgId, provider] as const,
  accountLink: (token: string) => ["account-link", token] as const,
  prompts: (orgId: string, repoId?: string) => ["prompts", orgId, repoId ?? ""] as const,
  chat: (chatId: string) => ["chat", chatId] as const,
  /** The chat's `TaskApproval`, or null when it never needed one. Not under
   * the `chat` prefix, so a chat-detail invalidation does not refetch it. */
  chatApprovals: ["chat-approval"] as const,
  chatApproval: (chatId: string) => ["chat-approval", chatId] as const,
  file: (worktreeId: string, path: string) => ["file", worktreeId, path] as const,
  /** One directory's children in the file browser. Read live from the worker,
   * so the socket patcher expires these with every snapshot. */
  dir: (worktreeId: string, path: string) => ["dir", worktreeId, path] as const,
  /** Prefix for every cached directory in one worktree. */
  dirs: (worktreeId: string) => ["dir", worktreeId] as const,
  /** Prefix for every cached file in one worktree. File content is only
   * invalidated by a new snapshot, so the socket patcher expires them as a set. */
  files: (worktreeId: string) => ["file", worktreeId] as const,

  /** Super-user console. Cross-org, so none of these are org-scoped and none
   * are patched by the org socket — the pages poll instead. Each list is keyed
   * by its whole query, so every page/filter/sort is its own cache entry of
   * the one page shape. */
  admin: {
    /** Prefix for everything below: query defaults and post-mutation invalidation. */
    all: () => ["admin"] as const,
    overview: () => ["admin", "overview"] as const,
    orgs: (query: AdminOrgListQuery) => ["admin", "orgs", query] as const,
    org: (orgId: string) => ["admin", "org", orgId] as const,
    users: (query: AdminUserListQuery) => ["admin", "users", query] as const,
    user: (userId: string) => ["admin", "user", userId] as const,
    workers: (query: AdminWorkerListQuery) => ["admin", "workers", query] as const,
    jobs: (query: AdminJobListQuery) => ["admin", "jobs", query] as const,
    audit: (query: AdminAuditListQuery) => ["admin", "audit", query] as const,
    prompts: () => ["admin", "prompts"] as const,
  },
};
